// @ts-nocheck
import { useNavigation } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import { View, ScrollView, FlatList, StyleSheet } from 'react-native';
import Text from '../../../components/Text';
import Icon from '@expo/vector-icons/Feather';
import Navbar from '../../../components/Navbar';
import ThemedSwipeablePanel from '../../../components/ThemedSwipeablePanel';
import { CreateDelivery, UpdateDelivery } from '../deliveriesReducer';
import { initialDateFrameState, initialDeliveryState } from '../data';
import { useDispatch, useSelector } from 'react-redux';
import { RootStore } from '../../../redux/store';
import { TouchableOpacity } from 'react-native-gesture-handler';
import { RouteProp, useRoute } from '@react-navigation/native';
import { RootStackParamList } from '../../../@types';
import { residentApiWithAuth } from '../../../api/resident';
import { showErrorMessage } from '../../../service/flashMessage';
import { isTablet } from 'react-native-device-info';
import Button from '../../../components/Button';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import TextArea from '../../../components/TextArea';
import VStack from '../../../components/VStack';
import Container from '../../../components/Container';
import DateTimeInput from '../../../components/DateTimeInput';
import Content from '../../../navigation/Content';
import FormContainer from '../../../components/FormContainer';

const AddDelivery: React.FC = () => {
  const { delivery } =
    useRoute<RouteProp<RootStackParamList, 'Add delivery'>>().params;
  const isInEditMode = !!delivery;
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const { id, unit } = useSelector(
    (state: RootStore) => state.units.unitLinks,
  ).filter(link => link.isDefault === true)[0];
  const { user } = useSelector((state: RootStore) => state.auth);
  const getDefaultDeliveryState = () => ({
    ...initialDeliveryState,
    unitId: unit.id, // unitId should be renamed to unitLinkId in the API
    personAuthorizing: user.id,
  });
  const getDefaultDateFrameState = () => ({
    ...initialDateFrameState,
    unitId: unit.id,
  });

  const [deliveryState, setDeliveryState] = useState(getDefaultDeliveryState);
  const [dateFrameState, setDateFrameState] = useState(getDefaultDateFrameState);

  const deliveriesState = useSelector((state: RootStore) => state.deliveries);
  const [title, setTitle] = useState('Add delivery');
  const [companiesPanelShown, setCompaniesPanelShown] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [duration, setDuration] = useState(0);
  // Needs to come from list site
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchCategories().then(() => fetchCompanies(unit.id));
  }, [id, unit.id]);

  useEffect(() => {
    if (!deliveryState.category) {
      const uniqueSet = new Set<string>();
      const uniqueCompanies = allCompanies.filter((item: any) => {
        const name = item.company?.toLowerCase();
        if (!name || uniqueSet.has(name)) return false;
        uniqueSet.add(name);
        return true;
      });
      setCompanies(uniqueCompanies);
    } else {
      const selectedCat = deliveryState.category.trim().toLowerCase();
      const matched = allCompanies.filter((item: any) => {
        if (!item.category) return false;
        const itemCat = item.category.trim().toLowerCase();
        return (
          itemCat === selectedCat ||
          itemCat.includes(selectedCat) ||
          selectedCat.includes(itemCat)
        );
      });

      const uniqueSet = new Set<string>();
      const filtered = matched.filter((item: any) => {
        const name = item.company?.toLowerCase();
        if (!name || uniqueSet.has(name)) return false;
        uniqueSet.add(name);
        return true;
      });
      setCompanies(filtered);
    }
  }, [deliveryState.category, allCompanies]);

  useEffect(() => {
    if (!isInEditMode) {
      setDeliveryState(getDefaultDeliveryState());
      setDateFrameState(getDefaultDateFrameState());
      setDuration(0);
      setTitle('Add delivery');
      return;
    }

    const deliveryObj = Object.assign({}, delivery);
    const nextDeliveryState = {
      ...getDefaultDeliveryState(),
      category: deliveryObj.category,
      authorizationTo: deliveryObj.authorizationTo,
      company: deliveryObj.company,
      expectedTime: deliveryObj.expectedTime,
      //@ts-ignore
      id: deliveryObj.id,
      instructions: deliveryObj.instructions,
      personAuthorizing: deliveryObj.personAuthorizing,
    };
    const nextDateFrameState = {
      ...getDefaultDateFrameState(),
      ...deliveryObj?.dateFrame,
    };
    const deliveryDuration =
      new Date(nextDateFrameState.endDate).getTime() -
      new Date(nextDateFrameState.startDate).getTime();

    setDeliveryState(nextDeliveryState);
    setDateFrameState(nextDateFrameState);
    setDuration(deliveryDuration > 0 ? Math.round(deliveryDuration / 60000) : 0);
    setTitle('Edit delivery');
  }, [delivery, isInEditMode, unit.id, user.id]);

  const fetchCompanies = async (unitId: string) => {
    try {
      const res = await residentApiWithAuth().get(
        `/deliveries/CompanyNamesInUnit/${unitId}`,
      );
      const data = res.data.data || [];
      const pastDeliveries = deliveriesState?.deliveries || [];

      const companyCatMap = new Map<string, Set<string>>();
      pastDeliveries.forEach((d: any) => {
        if (d.company) {
          const comp = d.company.trim();
          const cat = d.category?.trim() || '';
          const key = comp.toLowerCase();
          if (!companyCatMap.has(key)) {
            companyCatMap.set(key, new Set());
          }
          if (cat) {
            companyCatMap.get(key)!.add(cat);
          }
        }
      });

      const rawCompanies: Array<{ company: string; category?: string }> = [];
      const seenKeys = new Set<string>();

      pastDeliveries.forEach((d: any) => {
        if (d.company) {
          const comp = d.company.trim();
          const cat = d.category?.trim() || '';
          const key = `${comp.toLowerCase()}_${cat.toLowerCase()}`;
          if (!seenKeys.has(key)) {
            seenKeys.add(key);
            rawCompanies.push({ company: comp, category: cat });
          }
        }
      });

      data.forEach((item: any) => {
        if (item.company) {
          const comp = item.company.trim();
          const compKey = comp.toLowerCase();
          const cat = item.category?.trim() || '';

          const knownCats = companyCatMap.get(compKey);
          if (knownCats && knownCats.size > 0) {
            knownCats.forEach(c => {
              const k = `${compKey}_${c.toLowerCase()}`;
              if (!seenKeys.has(k)) {
                seenKeys.add(k);
                rawCompanies.push({ company: comp, category: c });
              }
            });
          } else {
            const k = `${compKey}_${cat.toLowerCase()}`;
            if (!seenKeys.has(k)) {
              seenKeys.add(k);
              rawCompanies.push({ company: comp, category: cat });
            }
          }
        }
      });

      setAllCompanies(rawCompanies);
    } catch (error) {
      showErrorMessage(String(error));
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await residentApiWithAuth().get(
        'listbysite?table=Deliveries',
      );
      const data = res.data.data;
      const options = data.filter(item => item.field === 'Category');
      const optionsList = options[0].options.split(',');
      setCategories(optionsList);
    } catch (error) {
      showErrorMessage(String(error));
    }
  };

  const updateState = (field: string, text: string) =>
    setDeliveryState({
      ...deliveryState,
      [field]: text,
    });

  const calcEndDate = (mins: number) => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + mins);

    setDateFrameState({
      ...dateFrameState,
      endDate: new Date(now).toISOString(),
    });
  };

  const handleDurationChange = (durationInMins: string) => {
    if (durationInMins === '') {
      setDuration(0);
      calcEndDate(0);
    } else {
      setDuration(parseInt(durationInMins, 10));
      calcEndDate(parseInt(durationInMins, 10));
    }
  };

  const CompaniesPanel = () => {
    return (
      <ThemedSwipeablePanel
        onlyLarge
        fullWidth
        style={{
          maxWidth: 1100,
        }}
        isActive={companiesPanelShown}
        onClose={() => setCompaniesPanelShown(false)}
        showCloseButton>
        <View style={{ paddingHorizontal: 16, paddingTop: 12, paddingBottom: 6 }}>
          <Text style={{ fontWeight: 'bold', fontSize: 16, color: '#18181b' }}>
            {deliveryState.category
              ? `Saved places for ${deliveryState.category}`
              : 'Saved delivery places'}
          </Text>
        </View>
        <FlatList
          data={companies}
          ListEmptyComponent={
            <View style={{ padding: 16, alignItems: 'center' }}>
              <Text style={{ color: '#71717a', fontSize: 14 }}>
                {deliveryState.category
                  ? `No saved places for ${deliveryState.category} yet.`
                  : 'No saved places found.'}
              </Text>
            </View>
          }
          renderItem={({ item, index }) => (
            <Select.Item
              key={item.company}
              label={item.company}
              value={item.company}
              onPress={() => {
                setCompaniesPanelShown(false);
                updateState('company', item.company);
              }}
            />
          )}
        />
      </ThemedSwipeablePanel>
    );
  };

  return (
    <Container>
      <Content>
        <Navbar
          title={title}
          navigation={navigation}
          backHandler={() => navigation.navigate('Dashboard')}
        />
        <FormContainer>
          <ScrollView
            style={styles.scrollView}
          >
            <>
              <Text style={[styles.textInputLabel, styles.marginTop]}>
                Category
              </Text>
              <Select
                selectedValue={deliveryState.category}
                minWidth="200"
                placeholder="Choose Category"
                mt={1}
                onValueChange={itemValue =>
                  setDeliveryState({ ...deliveryState, category: itemValue })
                }>
                {categories.map(category => (
                  <Select.Item label={category} value={category} key={category} />
                ))}
              </Select>
              <Text style={[styles.textInputLabel, styles.marginTop]}>
                From
              </Text>
              <Input
                minWidth={300}
                style={styles.flex}
                placeholder="eg. Asterix Inc"
                value={deliveryState.company}
                onChangeText={text => updateState('company', text)}
                InputRightElement={
                  <TouchableOpacity
                    style={styles.paddingHorizontal}
                    onPress={() => setCompaniesPanelShown(true)}>
                    <Icon name="chevron-down" size={24} color="#007bff" />
                  </TouchableOpacity>
                }
              />
              <DateTimeInput
                label="Delivery date"
                value={new Date(dateFrameState.startDate)}
                onConfirm={date => {
                  setDateFrameState({
                    ...dateFrameState,
                    startDate: new Date(date).toISOString(),
                    endDate:
                      duration === 0
                        ? new Date(date).toISOString()
                        : dateFrameState.endDate,
                  });
                }}
                mode="date"
              />

            </>
            <View style={styles.marginBottom} />
          </ScrollView>

          <CompaniesPanel />
          <Button
            onPress={() => {
              isInEditMode
                ? dispatch(UpdateDelivery(dateFrameState, deliveryState))
                : dispatch(CreateDelivery(dateFrameState, deliveryState));
            }}
            isLoading={deliveriesState.loading}
            isDisabled={deliveriesState.loading}
            isLoadingText="Saving ..."
            p="4"
            m="4"
            style={[styles.marginTop, styles.marginBottom]}
          >
            Save
          </Button>
        </FormContainer>
      </Content>
    </Container>

  );
};

const styles = StyleSheet.create({
  scrollView: {
    marginHorizontal: 8,
    marginBottom: 16,
  },
  textInputLabel: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  marginTop: {
    marginTop: 8,
  },
  flex: {
    flex: 1,
  },
  paddingHorizontal: {
    paddingHorizontal: 16,
  },
  flexRow: {
    flexDirection: 'row',
  },
  marginBottom: {
    marginBottom: 24,
  },
});

export default AddDelivery;
